import { z } from 'zod';

/**
 * Provably fair seeds, which belong to a session rather than to a game.
 *
 * The platform is the only source of randomness: it commits to a server seed
 * before a bet, hands the engine the material, and reveals the seed once it
 * has been retired. A player's seed pair follows them across games, so these
 * routes carry no game code.
 */

export const SeedCommitmentResponse = z.object({
  status: z.literal('OK'),
  serverSeedHash: z.string().regex(/^[0-9a-f]{64}$/),
  clientSeed: z.string(),
  nonce: z.string().regex(/^(?:0|[1-9]\d*)$/),
});
export type SeedCommitmentResponse = z.infer<typeof SeedCommitmentResponse>;

export const RotateClientSeedRequest = z.object({
  clientSeed: z.string().min(1).max(128),
});
export type RotateClientSeedRequest = z.infer<typeof RotateClientSeedRequest>;

/**
 * Rotating the server seed reveals the one it replaces. That is the whole
 * point of the rotation: until a seed is retired a player cannot check the
 * rounds it produced, and after it is retired they can check all of them.
 */
export const RotateServerSeedResponse = SeedCommitmentResponse.extend({
  retiredServerSeed: z.string().min(32),
  retiredServerSeedHash: z.string().regex(/^[0-9a-f]{64}$/),
});
export type RotateServerSeedResponse = z.infer<typeof RotateServerSeedResponse>;

/**
 * Everything needed to recompute one round.
 *
 * `proof` is what the engine recorded while deriving the outcome, opaque to
 * the platform; the seed fields are the platform's, and `serverSeed` appears
 * only once the seed has been retired.
 */
export const RoundProofResponse = z.object({
  status: z.literal('OK'),
  roundId: z.uuid(),
  gameCode: z.string(),
  mathVersion: z.string(),
  serverSeedHash: z.string().regex(/^[0-9a-f]{64}$/),
  serverSeed: z.string().nullable(),
  clientSeed: z.string(),
  nonce: z.string().regex(/^(?:0|[1-9]\d*)$/),
  proof: z.record(z.string(), z.unknown()).nullable(),
  result: z.record(z.string(), z.unknown()).nullable(),
});
export type RoundProofResponse = z.infer<typeof RoundProofResponse>;
